/**
 * Recensioni — pure logic: the review page's state machine, the live-review
 * thresholds and the formatting the sites share. No DOM, no React.
 *
 * Hard rule (spec §2): nothing here may branch on the rating. The page's
 * views and the Google invitation depend only on the request's state and on
 * whether the sede has a Google link.
 */
import type { ApiResult, PublicReview, PublicReviewList, ReviewRequestInfo } from './api'
import type { ReviewsLocale } from './strings'

export const TEXT_MAX = 2000
export const NAME_MAX = 60
/** The character counter appears from here on. */
export const TEXT_COUNTER_FROM = 1800

/* ── Live reviews on the sites ──────────────────────────────────────── */

/** Verde Vercelli 2 puts verified reviews in its strip from this many on (spec §12.3: from the first). */
export const VERCELLI_MIN_LIVE = 1
/** Verde Valsesia 2 shows its reviews section from this many on. */
export const VALSESIA_MIN_LIVE = 1

/** The list when it has at least `min` reviews to show, else null (the page stays as it is). */
export function liveOrNull(list: PublicReviewList | null, min: number): PublicReviewList | null {
  return list && list.reviews.length >= min ? list : null
}

/** `4.75` → `4,8` (it) / `4.8` (en). */
export function formatAverage(avg: number, locale: ReviewsLocale): string {
  return avg.toLocaleString(locale === 'it' ? 'it-IT' : 'en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

const MONTHS: Record<ReviewsLocale, readonly string[]> = {
  it: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
}

/** `2026-09` → `settembre 2026`; anything else → ''. */
export function formatMonth(date: string, locale: ReviewsLocale): string {
  const m = /^(\d{4})-(\d{2})/.exec(date)
  if (!m) return ''
  const month = MONTHS[locale][Number(m[2]) - 1]
  return month ? `${month} ${m[1]}` : ''
}

/** «Mario R., Vercelli» — the signature a published review carries. */
export function signature(name: string, town: string): string {
  return [name.trim(), town.trim()].filter(Boolean).join(', ')
}

/**
 * One loop of the marquee: the live reviews repeated until the loop holds at
 * least `minItems` cards, so a short list never leaves a gap on a wide
 * screen. `repeat` marks the copies (aria-hidden: read once, not three times).
 */
export function marqueeLoop<T>(reviews: readonly T[], minItems = 8): { review: T; repeat: boolean }[] {
  if (!reviews.length) return []
  const out: { review: T; repeat: boolean }[] = []
  while (out.length < Math.max(minItems, reviews.length)) {
    for (const review of reviews) out.push({ review, repeat: out.length >= reviews.length })
  }
  return out
}

/* ── Verde Vercelli 2: one strip, verified reviews first ──────────── */

export type ReviewSource = 'verified' | 'google'

/** One card of the strip, whatever its source. `meta` is town · month for a verified review, '' otherwise. */
export interface StripCard {
  key: string
  source: ReviewSource
  name: string
  meta: string
  rating: number
  text: string
}

/**
 * Our verified reviews (API order: newest first), then the reviews copied
 * from Google, each tagged with its source. Never mixes the two orders.
 */
export function mergeStrip(
  verified: readonly PublicReview[],
  google: readonly { name: string; rating: number; text: string }[],
  locale: ReviewsLocale,
): StripCard[] {
  return [
    ...verified.map((r): StripCard => ({
      key: `v${r.id}`,
      source: 'verified',
      name: r.display_name,
      meta: [r.town, formatMonth(r.date, locale)].filter(Boolean).join(' · '),
      rating: r.rating,
      text: r.text,
    })),
    ...google.map((g, i): StripCard => ({ key: `g${i}`, source: 'google', name: g.name, meta: '', rating: g.rating, text: g.text })),
  ]
}

/**
 * What the Google laurel shows. With a live rating: the number in the
 * locale's decimals and the rounded stars. Without one (no key, no place id,
 * any failure): null — the laurel keeps its label and link and shows no
 * number and no stars, never a stale or made-up figure.
 */
export function googleFigure(r: { rating: number; count: number } | null, locale: ReviewsLocale): { value: string; stars: number; count: number } | null {
  if (!r) return null
  return { value: formatAverage(r.rating, locale), stars: Math.round(r.rating), count: r.count }
}

/* ── The review page ────────────────────────────────────────────────── */

export type PageView =
  | { kind: 'loading' }
  | { kind: 'network' }
  | { kind: 'notFound' }
  | { kind: 'expired' | 'revoked'; info: ReviewRequestInfo | null }
  | { kind: 'open'; info: ReviewRequestInfo }
  | { kind: 'submitted'; info: ReviewRequestInfo | null; google: boolean; text: string; consent: boolean; published: boolean; displayName: string }

export type OptOutStep = 'idle' | 'confirm' | 'sending' | 'done' | 'error' | 'invalid'

export type SubmitError = 'network' | 'invalid' | 'throttled' | null

/** Withdrawing the publication consent from the submitted view. */
export type WithdrawStep = 'idle' | 'confirm' | 'sending' | 'done' | 'error'

export interface PageState {
  view: PageView
  submitting: boolean
  submitError: SubmitError
  submitDetail: string
  optOut: OptOutStep
  withdraw: WithdrawStep
  /** Bumped to make the page fetch the request again. */
  reload: number
}

export interface SentReview {
  text: string
  consent: boolean
  displayName: string
}

export type PageAction =
  | { type: 'reload' }
  | { type: 'loaded'; result: ApiResult<ReviewRequestInfo> }
  | { type: 'submit' }
  | { type: 'submitted'; result: ApiResult<{ google: boolean }>; sent: SentReview }
  | { type: 'optOut'; step: OptOutStep }
  | { type: 'optOutResult'; status: number; ok: boolean }
  | { type: 'withdraw'; step: 'idle' | 'confirm' | 'sending' }
  | { type: 'withdrawResult'; status: number; ok: boolean }

export function initialPageState(hash: string): PageState {
  return {
    view: { kind: 'loading' },
    submitting: false,
    submitError: null,
    submitDetail: '',
    optOut: hash === '#stop' ? 'confirm' : 'idle',
    withdraw: 'idle',
    reload: 0,
  }
}

/** What the page shows for a `GET /api/landing/review/<token>/` result. */
export function viewFor(result: ApiResult<ReviewRequestInfo>): PageView {
  if (!result.ok) {
    if (result.status === 404) return { kind: 'notFound' }
    if (result.status === 410) return { kind: 'expired', info: null }
    return { kind: 'network' }
  }
  const info = result.data
  switch (info.state) {
    case 'open':
      return { kind: 'open', info }
    case 'submitted':
      return {
        kind: 'submitted',
        info,
        google: info.google,
        text: info.review?.text ?? '',
        consent: info.review?.consent_publish ?? false,
        published: info.review?.published ?? false,
        displayName: info.review?.display_name || info.display_name,
      }
    case 'expired':
    case 'revoked':
      return { kind: info.state, info }
  }
}

/** First human-readable message in a DRF 400 body (`{detail}` or `{field: [msg]}`), else ''. */
export function firstMessage(body: unknown): string {
  if (typeof body === 'string') return body
  if (Array.isArray(body)) return body.length ? firstMessage(body[0]) : ''
  if (body && typeof body === 'object') {
    for (const v of Object.values(body as Record<string, unknown>)) {
      const m = firstMessage(v)
      if (m) return m
    }
  }
  return ''
}

export function pageReducer(state: PageState, action: PageAction): PageState {
  switch (action.type) {
    case 'reload':
      return { ...state, view: { kind: 'loading' }, submitting: false, withdraw: 'idle', reload: state.reload + 1 }
    case 'loaded':
      return { ...state, view: viewFor(action.result) }
    case 'submit':
      return { ...state, submitting: true, submitError: null, submitDetail: '' }
    case 'submitted': {
      const r = action.result
      if (r.ok) {
        const info = state.view.kind === 'open' ? state.view.info : null
        return {
          ...state,
          submitting: false,
          view: { kind: 'submitted', info, google: r.data.google, ...action.sent, published: action.sent.consent },
        }
      }
      // Already submitted (409) or no longer open (410): the server knows
      // the real state — fetch it again rather than guess.
      if (r.status === 409 || r.status === 410) return pageReducer({ ...state, submitting: false }, { type: 'reload' })
      if (r.status === 400) return { ...state, submitting: false, submitError: 'invalid', submitDetail: firstMessage(r.data) }
      if (r.status === 429) return { ...state, submitting: false, submitError: 'throttled', submitDetail: '' }
      return { ...state, submitting: false, submitError: 'network', submitDetail: '' }
    }
    case 'optOut':
      return { ...state, optOut: action.step }
    case 'optOutResult':
      return { ...state, optOut: action.ok ? 'done' : action.status === 404 ? 'invalid' : 'error' }
    case 'withdraw':
      return { ...state, withdraw: action.step }
    case 'withdrawResult': {
      if (action.ok) {
        const view = state.view.kind === 'submitted' ? { ...state.view, consent: false, published: false } : state.view
        return { ...state, view, withdraw: 'done' }
      }
      if (action.status === 404) return { ...state, view: { kind: 'notFound' }, withdraw: 'idle' }
      // 409 (no review on this request): the page is out of date — ask the server.
      if (action.status === 409) return pageReducer(state, { type: 'reload' })
      return { ...state, withdraw: 'error' }
    }
  }
}

/**
 * The Valsesia reviews section goes in just above the contact band. It is
 * inserted only while that point is below the viewport, so nothing the
 * visitor is looking at moves (a deep link to #contatti, browsers without
 * scroll anchoring). `top` is the band's top relative to the viewport.
 */
export function canInsertAbove(top: number | null, viewportHeight: number): boolean {
  return top === null || top >= viewportHeight
}

/** The submit button only refuses a missing rating; the text is optional. */
export function canSubmit(rating: number): boolean {
  return Number.isInteger(rating) && rating >= 1 && rating <= 5
}

/** Next rating for an arrow/Home/End key in the star radiogroup (wraps like native radios); null for other keys. */
export function ratingForKey(key: string, current: number): number | null {
  const from = current >= 1 && current <= 5 ? current : 0
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return from >= 5 ? 1 : from + 1
    case 'ArrowLeft':
    case 'ArrowDown':
      return from <= 1 ? 5 : from - 1
    case 'Home':
      return 1
    case 'End':
      return 5
    default:
      return null
  }
}
